/**
 * Product Asset Scheduler - Main Application
 * Shopify Plus app for scheduling product collections and storefront previews
 * 
 * MODERN AUTH (2024+):
 * Uses CDN-hosted App Bridge (app-bridge.js) which is auto-initialized.
 * No AppBridgeProvider wrapper needed - the global `shopify` object is
 * automatically available when embedded in Shopify Admin.
 */

import React, { useState, useCallback, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AppProvider, Frame, Toast, Banner, Button, Text, Spinner, Modal, TextField, Select, BlockStack, InlineStack, Popover, ActionList, Icon } from '@shopify/polaris';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import enTranslations from '@shopify/polaris/locales/en.json';
import { ChatMajor, SettingsMajor } from '@shopify/polaris-icons';
import { initializeAuth, isShopifyAvailable, getShopDomain, probeShopifyAuth } from './utils/authenticated-fetch';
import { useAutoProcessor } from './hooks/useAutoProcessor';
import commerceDashSquareIcon from '../commerce-dash-icon-square.png';
import CrispChat, { CRISP_UNAVAILABLE_EVENT, isCrispConfigured, openCrispSupport } from './components/common/CrispChat';

// Pages (lazy loaded to improve initial load performance)
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Scheduler = lazy(() => import('./pages/Scheduler'));
const ThemeScheduler = lazy(() => import('./pages/ThemeScheduler'));
const VisualSectionScheduler = lazy(() => import('./pages/VisualSectionScheduler'));
const CalendarPage = lazy(() => import('./pages/Calendar'));
const Preview = lazy(() => import('./pages/Preview'));
const Activity = lazy(() => import('./pages/Activity'));
const Settings = lazy(() => import('./pages/Settings'));
  const Pricing = lazy(() => import('./pages/Pricing'));
import ErrorBoundary from './components/common/ErrorBoundary';

// Contexts
import { UserProvider } from './contexts/UserContext';
import { useUser, useUserPreferences } from './contexts/UserContext';
import { getShopDomain as getSupabaseShopDomain } from './utils/supabase';
import { withShopifyContext } from './utils/shopify-navigation';

// Types
import type { ToastMessage } from './types';

// Styles
import './styles/global.css';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 60 * 1000,
    },
  },
});

const APP_NAV_ITEMS = [
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'Asset Scheduler', path: '/sections' },
  { label: 'Collection Scheduler', path: '/scheduler' },
  { label: 'Calendar', path: '/calendar' },
  { label: 'Activity', path: '/activity' },
  { label: 'Billing', path: '/pricing' },
  { label: 'Settings', path: '/settings' },
] as const;

const ACCOUNT_TIMEZONE_OPTIONS = [
  { label: 'Eastern Time (ET)', value: 'America/New_York' },
  { label: 'Central Time (CT)', value: 'America/Chicago' },
  { label: 'Mountain Time (MT)', value: 'America/Denver' },
  { label: 'Pacific Time (PT)', value: 'America/Los_Angeles' },
  { label: 'Alaska Time (AKT)', value: 'America/Anchorage' },
  { label: 'Hawaii Time (HT)', value: 'Pacific/Honolulu' },
  { label: 'UTC', value: 'UTC' },
  { label: 'London (GMT/BST)', value: 'Europe/London' },
  { label: 'Paris / Berlin (CET)', value: 'Europe/Paris' },
  { label: 'Athens (EET)', value: 'Europe/Athens' },
  { label: 'Dubai (GST)', value: 'Asia/Dubai' },
  { label: 'India (IST)', value: 'Asia/Kolkata' },
  { label: 'Singapore (SGT)', value: 'Asia/Singapore' },
  { label: 'Tokyo (JST)', value: 'Asia/Tokyo' },
  { label: 'Sydney (AEST/AEDT)', value: 'Australia/Sydney' },
  { label: 'Auckland (NZST/NZDT)', value: 'Pacific/Auckland' },
];

function buildAdminNavHref(path: string): string {
  if (typeof window === 'undefined') return path;
  return withShopifyContext(path);
}

function AppBridgeNavMenu() {
  const location = useLocation();
  const hasEmbeddedContext = typeof window !== 'undefined' && (
    Boolean(window.shopify) || new URLSearchParams(window.location.search).has('host')
  );

  if (!hasEmbeddedContext) return null;

  return React.createElement(
    'ui-nav-menu',
    null,
    ...APP_NAV_ITEMS.map((item, index) => React.createElement('a', {
      key: item.path,
      href: buildAdminNavHref(item.path),
      rel: index === 0 ? 'home' : undefined,
      'aria-current': location.pathname === item.path ? 'page' : undefined,
    }, item.label)),
  );
}

function CommerceDashMark() {
  return (
    <div
      aria-label="Commerce Dash"
      className="catalogiq-brand-mark"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        minHeight: 40,
        minWidth: 248,
        width: 248,
        flexWrap: 'nowrap',
        overflow: 'hidden',
      }}
    >
      <span
        aria-hidden="true"
        className="catalogiq-brand-mark__icon"
        style={{
          display: 'block',
          width: 28,
          height: 28,
          minWidth: 28,
          minHeight: 28,
          flexShrink: 0,
          backgroundImage: `url(${commerceDashSquareIcon})`,
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'contain',
          lineHeight: 1,
        }}
      />
      <span
        className="catalogiq-brand-mark__wordmark"
        style={{
          color: 'var(--brand-ink, #20324d)',
          fontSize: 26,
          fontWeight: 700,
          lineHeight: 1,
          whiteSpace: 'nowrap',
          textRendering: 'geometricPrecision',
          display: 'inline-flex',
          minWidth: 0,
          flex: '0 0 auto',
        }}
      >
        Commerce Dash
      </span>
    </div>
  );
}

// Note: Embedded vs non-embedded handling is now centralized in AppBridgeInit.

/**
 * Component that initializes modern App Bridge for authenticated API calls
 * The CDN-hosted App Bridge auto-initializes, so we just need to:
 * 1. Wait for the shopify global to be available
 * 2. Initialize our auth module
 * 3. Verify shop domain was detected
 */
function AppBridgeInit({ children }: { children: React.ReactNode }) {
  const [shopDomainStatus, setShopDomainStatus] = useState<'checking' | 'ok' | 'missing'>('checking');
  const [appBridgeReady, setAppBridgeReady] = useState(false);
  const [authProbe, setAuthProbe] = useState<
    { status: 'checking' }
    | { status: 'ok' }
    | { status: 'install_required'; shopDomain: string; installUrl: string }
    | { status: 'error'; error: string }
  >({ status: 'checking' });
  
  useEffect(() => {
    // Poll for shopify global (CDN script may load after React)
    let attempts = 0;
    const maxAttempts = 20; // 2 seconds max wait
    
    const checkShopify = () => {
      attempts++;
      
      if (isShopifyAvailable()) {
        console.log('[App] ✅ window.shopify is available');
        setAppBridgeReady(true);
        initializeAuth();
        
        // Check if shop domain was successfully extracted
        setTimeout(() => {
          const detectedShop = getShopDomain();
          
          if (detectedShop) {
            console.log('[App] ✅ Shop domain detected:', detectedShop);
            setShopDomainStatus('ok');
          } else {
            console.error('[App] ❌ Failed to detect shop domain from any source');
            setShopDomainStatus('missing');
          }
        }, 100);
        return;
      }
      
      if (attempts < maxAttempts) {
        console.log(`[App] Waiting for window.shopify... (attempt ${attempts}/${maxAttempts})`);
        setTimeout(checkShopify, 100);
      } else {
        console.warn('[App] ⚠️ window.shopify not available after 2s - may not be embedded');
        // Still try to initialize auth (may get shop from URL params or env)
        initializeAuth();
        setAppBridgeReady(true);
        
        const detectedShop = getShopDomain();
        setShopDomainStatus(detectedShop ? 'ok' : 'missing');
      }
    };
    
    checkShopify();
  }, []);

  // Once we know the shop domain, do a single probe to detect missing install/auth
  useEffect(() => {
    if (!appBridgeReady) return;
    if (shopDomainStatus !== 'ok') return;
    let cancelled = false;

    (async () => {
      setAuthProbe({ status: 'checking' });
      const result = await probeShopifyAuth();
      if (cancelled) return;

      if (result.status === 'ok') setAuthProbe({ status: 'ok' });
      else if (result.status === 'install_required') setAuthProbe(result);
      else setAuthProbe({ status: 'error', error: result.error });
    })();

    return () => {
      cancelled = true;
    };
  }, [appBridgeReady, shopDomainStatus]);
  
  // Show loading state while checking for App Bridge
  if (!appBridgeReady) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <p>Initializing Shopify App Bridge...</p>
      </div>
    );
  }
  
  // Show warning banner if shop domain is missing
  if (shopDomainStatus === 'missing') {
    return (
      <>
        <div style={{ padding: 12 }}>
          <Banner title="Shop Configuration Missing" tone="critical">
            <p>
              Unable to determine the shop domain. The app will not be able to load or save data.
            </p>
            <p style={{ marginTop: 8 }}>
              <strong>Possible solutions:</strong>
            </p>
            <ul style={{ marginLeft: 20, marginTop: 4 }}>
              <li>Set <code>VITE_SHOPIFY_SHOP_DOMAIN</code> in environment variables</li>
              <li>Ensure the app is properly embedded in Shopify Admin</li>
              <li>Check the browser console for detailed error messages</li>
            </ul>
          </Banner>
        </div>
        {children}
      </>
    );
  }

  // Show install/reauthorize banner if the proxy indicates missing auth
  if (authProbe.status === 'install_required') {
    return (
      <>
        <div style={{ padding: 12 }}>
          <Banner title="Install or Reauthorize Required" tone="critical">
            <p>
              This shop isn’t authorized yet, so Shopify API requests can’t run.
            </p>
            <div style={{ marginTop: 8 }}>
              <Button
                variant="primary"
                onClick={() => window.open(authProbe.installUrl, '_top')}
              >
                Install / Reauthorize
              </Button>
            </div>
            <p style={{ marginTop: 8 }}>
              Shop: <strong>{authProbe.shopDomain}</strong>
            </p>
          </Banner>
        </div>
        {children}
      </>
    );
  }

  // If probe errored (but we have shop), show a non-blocking warning
  if (authProbe.status === 'error') {
    return (
      <>
        <div style={{ padding: 12 }}>
          <Banner title="Shopify Connection Issue" tone="warning">
            <p>
              Some Shopify data may fail to load. Details: {authProbe.error}
            </p>
          </Banner>
        </div>
        {children}
      </>
    );
  }
  
  return <>{children}</>;
}

function App() {
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [accountOpen, setAccountOpen] = useState(false);
  const [supabaseStatus, setSupabaseStatus] = useState<'ok' | 'missing' | 'invalid' | 'checking'>('checking');
  const [supabaseError, setSupabaseError] = useState<string | null>(null);
  const [appStylesLoaded, setAppStylesLoaded] = useState(true);

  const showToast = useCallback((message: ToastMessage) => {
    setToast(message);
  }, []);

  const hideToast = useCallback(() => {
    setToast(null);
  }, []);

  useAutoProcessor((message) => {
    showToast({
      content: message.message,
      error: message.type === 'error',
    });
  });

  // Runtime check for Supabase env keys and quick validity test
  useEffect(() => {
    const url = (import.meta && import.meta.env && import.meta.env.VITE_SUPABASE_URL) || '';
    const key = (import.meta && import.meta.env && import.meta.env.VITE_SUPABASE_ANON_KEY) || '';
    const shopDomain = (import.meta && import.meta.env && import.meta.env.VITE_SHOPIFY_SHOP_DOMAIN) || '';

    const afterPaint = (cb: () => void) => {
      if (typeof window === 'undefined') return cb();
      if (typeof window.requestAnimationFrame !== 'function') return setTimeout(cb, 0);
      window.requestAnimationFrame(() => window.requestAnimationFrame(cb));
    };

    if (!url || !key) {
      setSupabaseStatus('missing');
      setSupabaseError('VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY not set');
      return;
    }
    
    // Critical: Validate shop domain is configured
    if (!shopDomain) {
      console.warn('[App] ⚠️ VITE_SHOPIFY_SHOP_DOMAIN not set - will attempt to extract from App Bridge');
      // Don't fail immediately - App Bridge might provide it
      // But log warning so we can debug if data doesn't load
    }

    let cancelled = false;

    // Defer the probe until after first paint to reduce initial load work.
    afterPaint(() => {
      if (cancelled) return;
      // Quick probe to detect invalid API key (401 response)
      (async () => {
        try {
          const probeUrl = `${url.replace(/\/$/, '')}/rest/v1/scheduled_collection_assignments?select=id&limit=1`;
          const res = await fetch(probeUrl, {
            method: 'GET',
            headers: {
              apikey: key,
              Authorization: `Bearer ${key}`,
            },
          });

          if (cancelled) return;

          if (res.status === 401) {
            setSupabaseStatus('invalid');
            const text = await res.text();
            setSupabaseError(text || 'Invalid API key (401)');
          } else {
            setSupabaseStatus('ok');
            setSupabaseError(null);
          }
        } catch (err) {
          if (cancelled) return;
          setSupabaseStatus('invalid');
          setSupabaseError(err instanceof Error ? err.message : String(err));
        }
      })();
    });

    return () => { cancelled = true; };
  }, []);

  // Detect whether our global stylesheet is actually applied.
  // If missing, the UI will look like "old" Polaris defaults.
  useEffect(() => {
    try {
      const rootStyles = window.getComputedStyle(document.documentElement);
      const twRadius = rootStyles.getPropertyValue('--tw-radius').trim();
      const appShellBg = rootStyles.getPropertyValue('--app-shell-bg').trim();
      setAppStylesLoaded(Boolean(twRadius) && Boolean(appShellBg));
    } catch {
      setAppStylesLoaded(true);
    }
  }, []);

  const toastMarkup = toast ? (
    <div role="status" aria-live="polite" aria-atomic="true">
      <Toast
        content={toast.content}
        error={toast.error}
        duration={toast.duration || 4000}
        onDismiss={() => {
          hideToast();
          toast.onDismiss?.();
        }}
      />
    </div>
  ) : null;

  const buildSha = String((import.meta as any)?.env?.VITE_BUILD_SHA || '');
  const buildTime = String((import.meta as any)?.env?.VITE_BUILD_TIME || '');
  const buildLabel = buildSha ? buildSha.slice(0, 7) : 'dev';

  const AppContent = (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <UserProvider>
          <Router>
            <a className="skip-to-content" href="#main-content">
              Skip to main content
            </a>
            <AppBridgeNavMenu />
            <Frame>
              <ShellTopHeader onOpenAccount={() => setAccountOpen(true)} />
              {(supabaseStatus === 'missing' || supabaseStatus === 'invalid') && (
                <div style={{ padding: 12 }}>
                  <Banner title={supabaseStatus === 'missing' ? 'Supabase configuration missing' : 'Supabase API key problem'}>
                    <p>
                      {supabaseStatus === 'missing'
                        ? 'VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY are not set. Add them to your hosting environment variables and redeploy.'
                        : `Failed to validate Supabase anon key: ${supabaseError || 'Unknown error'}`}
                    </p>
                    <div style={{ marginTop: 8 }}>
                      <Button
                        variant="secondary"
                        onClick={() => window.location.reload()}
                      >
                        Reload Page
                      </Button>
                    </div>
                  </Banner>
                </div>
              )}
              {!appStylesLoaded && (
                <div style={{ padding: 12 }}>
                  <Banner title="App styles did not load" tone="warning">
                    <p>
                      The app is running the latest build, but the custom UI stylesheet is not applied.
                      This usually points to a cached or blocked CSS asset.
                    </p>
                  </Banner>
                </div>
              )}
              <main id="main-content" tabIndex={-1}>
                <Suspense
                  fallback={
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                      <Spinner size="large" />
                    </div>
                  }
                >
                  <Routes>
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route path="/dashboard" element={<Dashboard showToast={showToast} />} />
                    <Route path="/scheduler" element={<Scheduler showToast={showToast} />} />
                    <Route path="/themes" element={<ThemeScheduler showToast={showToast} />} />
                    <Route path="/sections" element={<VisualSectionScheduler showToast={showToast} />} />
                    {/* Theme Editor - commented out for now
                    <Route path="/editor" element={<VisualThemeEditor showToast={showToast} />} />
                    */}
                    {/* Discount scheduling disabled for now
                    <Route path="/discounts" element={<Discounts showToast={showToast} />} />
                    */}
                    <Route path="/calendar" element={<CalendarPage showToast={showToast} />} />
                    <Route path="/preview" element={<Preview showToast={showToast} />} />
                    <Route path="/activity" element={<Activity showToast={showToast} />} />
                    <Route path="/settings" element={<Settings showToast={showToast} />} />
                    <Route path="/pricing" element={<Pricing showToast={showToast} />} />
                  </Routes>
                </Suspense>
              </main>
              <div style={{ padding: '0 12px 12px', display: 'flex', justifyContent: 'flex-end' }}>
                <Text as="p" variant="bodySm" tone="subdued">
                  Build {buildLabel}{buildTime ? ` • ${buildTime}` : ''} • Styles {appStylesLoaded ? 'ok' : 'missing'}
                </Text>
              </div>
              <AccountModal open={accountOpen} onClose={() => setAccountOpen(false)} />
              {toastMarkup}
            </Frame>
          </Router>
        </UserProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );

  return (
    <AppProvider i18n={enTranslations}>
      <AppBridgeInit>
        {AppContent}
      </AppBridgeInit>
    </AppProvider>
  );
}

function ShellTopHeader({ onOpenAccount }: { onOpenAccount: () => void }) {
  const navigate = useNavigate();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const supportShopDomain = getSupabaseShopDomain() || getShopDomain() || '';
  const planName = 'LaunchIQ';

  const openSupportFallback = useCallback(() => {
    const subject = encodeURIComponent('LaunchIQ support');
    const body = encodeURIComponent(`Shop: ${supportShopDomain || 'Unavailable'}`);
    window.open(`mailto:support@commercedash.com?subject=${subject}&body=${body}`, '_blank');
  }, [supportShopDomain]);

  const handleOpenSupport = useCallback(() => {
    setAccountMenuOpen(false);

    if (isCrispConfigured() && openCrispSupport()) {
      return;
    }

    openSupportFallback();
  }, [openSupportFallback]);

  useEffect(() => {
    const handleCrispUnavailable = () => {
      openSupportFallback();
    };

    window.addEventListener(CRISP_UNAVAILABLE_EVENT, handleCrispUnavailable);
    return () => window.removeEventListener(CRISP_UNAVAILABLE_EVENT, handleCrispUnavailable);
  }, [openSupportFallback]);

  return (
    <>
      <CrispChat enabled shopDomain={supportShopDomain} planName={planName} />
      <div
        className="global-top-header"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          minHeight: 64,
          padding: '12px 24px',
          borderBottom: '1px solid rgba(60, 132, 246, 0.12)',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(243,249,255,0.96) 100%)',
          position: 'sticky',
          top: 0,
          zIndex: 50,
          boxShadow: '0 1px 2px rgba(60, 132, 246, 0.08), 0 10px 30px rgba(56, 198, 238, 0.08)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
          <CommerceDashMark />
        </div>
        <div className="catalogiq-header-actions">
          <button
            type="button"
            className="catalogiq-header-button catalogiq-header-button--support"
            onClick={handleOpenSupport}
          >
            <span className="catalogiq-header-button__icon" aria-hidden="true">
              <Icon source={ChatMajor} tone="base" />
            </span>
            <span>Support</span>
          </button>
          <Popover
            active={accountMenuOpen}
            activator={(
            <button
              type="button"
              onClick={() => setAccountMenuOpen((prev) => !prev)}
              className="catalogiq-header-account-button"
            >
              <InlineStack gap="100" blockAlign="center">
                <span className="catalogiq-header-account-avatar">
                  <span className="catalogiq-header-account-avatar-icon">
                    <Icon source={SettingsMajor} tone="base" />
                  </span>
                </span>
                <span className="catalogiq-header-account-label">Account</span>
              </InlineStack>
            </button>
            )}
            onClose={() => setAccountMenuOpen(false)}
            preferredAlignment="right"
          >
            <ActionList
              items={[
                {
                  content: 'Account Settings',
                  onAction: () => {
                    setAccountMenuOpen(false);
                    onOpenAccount();
                  },
                },
                {
                  content: 'App Settings',
                  onAction: () => {
                    setAccountMenuOpen(false);
                    navigate(withShopifyContext('/settings'));
                  },
                },
                {
                  content: 'Billing & Plans',
                  onAction: () => {
                    setAccountMenuOpen(false);
                    navigate(withShopifyContext('/pricing'));
                  },
                },
                {
                  content: 'Support',
                  onAction: handleOpenSupport,
                },
              ]}
            />
          </Popover>
        </div>
      </div>
    </>
  );
}

function AccountModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { user, isLoading: isUserLoading, saveAccountProfile } = useUser();
  const { preferences } = useUserPreferences();
  const [isSaving, setIsSaving] = useState(false);
  const [name, setName] = useState('');
  const [timezone, setTimezone] = useState('America/New_York');
  const trimmedName = name.trim();
  const requiresName = !isUserLoading && Boolean(user) && !(user?.name?.trim());
  const effectiveOpen = open || requiresName;

  useEffect(() => {
    if (!effectiveOpen) return;
    setName(user?.name || '');
    setTimezone(preferences.timezone || 'America/New_York');
  }, [effectiveOpen, user?.name, preferences.timezone]);

  const handleClose = useCallback(() => {
    if (requiresName) {
      return;
    }

    onClose();
  }, [onClose, requiresName]);

  const handleSave = useCallback(async () => {
    if (!user) return;
    if (!trimmedName) return;

    setIsSaving(true);
    try {
      await saveAccountProfile({ name: trimmedName, timezone });
      onClose();
    } finally {
      setIsSaving(false);
    }
  }, [onClose, saveAccountProfile, timezone, trimmedName, user]);

  return (
    <Modal
      open={effectiveOpen}
      onClose={handleClose}
      title="Account"
      primaryAction={{ content: 'Save', onAction: handleSave, loading: isSaving, disabled: !trimmedName }}
      secondaryActions={requiresName ? [] : [{ content: 'Cancel', onAction: handleClose }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {requiresName && (
            <Text as="p" variant="bodyMd" tone="subdued">
              Add your name before continuing. It will be saved for this store and follow your account across devices.
            </Text>
          )}
          <TextField label="Store" value={getSupabaseShopDomain() || ''} onChange={() => {}} disabled autoComplete="off" />
          <TextField
            label="Your Name"
            value={name}
            onChange={setName}
            autoComplete="name"
            error={!trimmedName ? 'Your name is required.' : undefined}
          />
          <Select
            label="Timezone"
            options={ACCOUNT_TIMEZONE_OPTIONS}
            value={timezone}
            onChange={setTimezone}
          />
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

export default App;


